import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

interface CreditsData {
  credits_balance: number;
  credits_from_membership: number;
  credits_from_purchases: number;
  membership_credits_reset_date: string | null;
}

interface TokenPurchase {
  id: string;
  package_name: string;
  tokens_purchased: number;
  tokens_remaining: number;
  purchased_at: string;
  expires_at: string;
  is_expired: boolean;
}

// Monthly token allocations by role
const MONTHLY_TOKEN_ALLOCATIONS = {
  free: 5,        // 5 free trial tokens (one-time)
  student: 500,   // 500 tokens/month for student/educator members
  paid: 500,      // 500 tokens/month for standard members
  admin: 99999,   // Essentially unlimited for admins
};

export function useSupercalcCredits() {
  const { user } = useAuth();
  const { role } = useUserRole();
  const [credits, setCredits] = useState<CreditsData | null>(null);
  const [purchases, setPurchases] = useState<TokenPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setCredits(null);
      setPurchases([]);
      setLoading(false);
      return;
    }

    try {
      // Fetch credits
      const { data: creditsData, error: creditsError } = await supabase
        .from('supercalc_credits')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (creditsError && creditsError.code !== 'PGRST116') {
        throw creditsError;
      }

      // Fetch active purchases
      const { data: purchasesData, error: purchasesError } = await supabase
        .from('supercalc_token_purchases')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_expired', false)
        .gt('tokens_remaining', 0)
        .order('expires_at', { ascending: true });

      if (purchasesError) {
        throw purchasesError;
      }

      setCredits(creditsData);
      setPurchases(purchasesData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Calculate total available credits
  const totalCredits = credits?.credits_balance || 0;
  
  // Get monthly allocation based on role
  const monthlyAllocation = MONTHLY_TOKEN_ALLOCATIONS[role as keyof typeof MONTHLY_TOKEN_ALLOCATIONS] || 0;
  
  // Check if user has credits available
  const hasCredits = totalCredits > 0;
  
  // Check if user is a member (student or paid)
  const isMember = role === 'student' || role === 'paid' || role === 'admin';
  
  // Calculate purchased tokens remaining (non-expired)
  const purchasedTokensRemaining = purchases.reduce((sum, p) => sum + p.tokens_remaining, 0);

  // Check if user needs to top up (low credits warning)
  const needsTopUp = isMember && totalCredits < 10;
  
  // Get next expiring purchase
  const nextExpiring = purchases.length > 0 ? purchases[0] : null;

  return {
    credits,
    purchases,
    loading,
    error,
    totalCredits,
    monthlyAllocation,
    hasCredits,
    isMember,
    purchasedTokensRemaining,
    needsTopUp,
    nextExpiring,
    refetch: fetchCredits,
  };
}

// Token package definitions for UI
export const TOKEN_PACKAGES = [
  {
    id: 'starter',
    name: 'Starter Pack',
    tokens: 300,
    price: 4.97,
    pricePerToken: (4.97 / 300).toFixed(4),
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro Pack',
    tokens: 4000,
    price: 49,
    pricePerToken: (49 / 4000).toFixed(4),
    popular: true,
    savings: '18%',
  },
  {
    id: 'power',
    name: 'Power Pack',
    tokens: 10000,
    price: 99,
    pricePerToken: (99 / 10000).toFixed(4),
    popular: false,
    savings: '40%',
  },
];
